import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Star, X } from "lucide-react";
import { A11y, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";

/**
 * Full-screen image modal with close button and ESC key support.
 */
function ImageModal({ src, onClose }) {
  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
    >
      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
        aria-label="Close image preview"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Image container */}
      <img
        src={src}
        alt="Review media full size"
        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/** Lexi's Google Business listing (all reviews in full). */
export const LEXI_GOOGLE_REVIEWS_URL =
  "https://maps.google.com/?cid=4364845442863869357";

/**
 * Real Google reviews, copied in by hand by the owner; excerpts only, never reworded.
 * A shortened review marks every cut with "…" and keeps the reviewer's own spelling.
 * Keep all of them (no cherry-picking); the full text is on Google (LEXI_GOOGLE_REVIEWS_URL).
 * Reviewers are shown by initials only (owner, 2 Nov 2026), with a plain initials avatar,
 * never their Google name or photo. "Where You Are Podcast" is a business account.
 */
export const LEXI_GOOGLE_REVIEWS = [
  {
    id: "mt",
    name: "M.T.",
    rating: 5,
    text: "It's Great. Works really well. Thanks",
  },
  {
    id: "mb",
    name: "M.B.",
    rating: 5,
    text: "Great for beginners or pro’s.",
  },
  {
    id: "wyap",
    name: "Where You Are Podcast",
    rating: 5,
    text: "",
  },
  {
    id: "mn",
    name: "M.N.",
    rating: 5,
    text: "",
  },
  {
    id: "ab",
    name: "A.B.",
    rating: 5,
    // Excerpt (owner, 2 Nov 2026); full review on Google.
    text: "Such an impressive app… The guessing and reading through the form totally eliminated and instead we could enjoy our day at the races… Great app - couldn't rate more highly!!",
    // His attached photo (a winning tip with a trophy) is left out (owner, 2 Nov 2026).
  },
  {
    id: "dg",
    name: "D.G.",
    rating: 5,
    // Excerpt (owner, 2 Nov 2026); full review on Google.
    text: "AI racing form is the way of the future to how we do form these days… highly recommend…",
  },
];

/** "M.T." -> "MT"; "Where You Are Podcast" -> "WP" (first and last word). */
export function reviewerInitials(name) {
  const words = String(name ?? "")
    .split(/[\s.]+/)
    .filter(Boolean);
  if (!words.length) return "";
  const first = words[0][0];
  const last = words.length > 1 ? words[words.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
}

/** Neutral initials circle in place of the reviewer's Google photo. */
function InitialsAvatar({ name, sizePx = 36, className = "" }) {
  return (
    <div
      aria-hidden="true"
      className={`flex flex-none items-center justify-center rounded-full bg-gray-100 border border-black/5 font-semibold text-gray-600 ${className}`}
      style={{
        width: `${sizePx}px`,
        height: `${sizePx}px`,
        fontSize: `${Math.round(sizePx * 0.36)}px`,
      }}
    >
      {reviewerInitials(name)}
    </div>
  );
}

function normalizeReviewText(text) {
  if (!text) return "";
  return String(text).trim();
}

function htmlBreaksToNewlines(text) {
  return String(text ?? "").replace(/<br\s*\/?>/gi, "\n");
}

function stripHtmlBreaks(text) {
  // Remove <br/> tags but preserve \n newlines
  return String(text ?? "")
    .replace(/<br\s*\/?>/gi, "") // Remove <br/> tags
    .replace(/[^\S\n]+/g, " ") // Collapse spaces/tabs but not newlines
    .trim();
}

function normalizeLine(line) {
  return String(line ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

function truncateLineAtWordBoundary(line, maxChars) {
  const normalized = normalizeLine(line);
  if (!normalized) return "";
  if (normalized.length <= maxChars) return normalized;

  const limit = Math.max(0, maxChars - 1);
  const head = normalized.slice(0, limit).trimEnd();
  const lastSpace = head.lastIndexOf(" ");
  const safe = lastSpace > 0 ? head.slice(0, lastSpace).trimEnd() : head;

  return `${safe}…`;
}

function getReviewPreview(text, maxChars) {
  const raw = normalizeReviewText(text);
  if (!raw) return { preview: "", isTruncated: false, hasText: false };

  const withNewlines = htmlBreaksToNewlines(raw);
  const lines = withNewlines
    .split("\n")
    .map(normalizeLine)
    .filter(Boolean);

  if (!lines.length) return { preview: "", isTruncated: false, hasText: false };

  // If there is a <br/>, prefer showing the first line + ellipsis.
  if (lines.length > 1) {
    const firstLine = lines[0];
    const firstLineFits = normalizeLine(firstLine).length <= Math.max(0, maxChars - 1);
    if (firstLineFits) {
      return { preview: `${normalizeLine(firstLine)}…`, isTruncated: true, hasText: true };
    }
    return {
      preview: truncateLineAtWordBoundary(firstLine, maxChars),
      isTruncated: true,
      hasText: true,
    };
  }

  const single = lines[0];
  const normalizedSingle = normalizeLine(single);
  if (normalizedSingle.length <= maxChars) {
    return { preview: normalizedSingle, isTruncated: false, hasText: true };
  }

  return {
    preview: truncateLineAtWordBoundary(normalizedSingle, maxChars),
    isTruncated: true,
    hasText: true,
  };
}

function StarRow({ rating = 5, sizePx = 14, gapPx = 3, color = "#F7B500" }) {
  const stars = Array.from({ length: 5 }, (_, i) => i < rating);
  return (
    <div
      className="inline-flex items-center"
      style={{ gap: `${gapPx}px` }}
      aria-label={`${rating} out of 5 stars`}
    >
      {stars.map((filled, idx) => (
        <Star
          key={idx}
          className={filled ? "" : "text-gray-300"}
          style={{
            width: `${sizePx}px`,
            height: `${sizePx}px`,
            ...(filled ? { color } : {}),
          }}
          fill={filled ? color : "none"}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches,
  );
  useEffect(() => {
    const mql = window.matchMedia?.(REDUCED_MOTION_QUERY);
    if (!mql) return undefined;
    const onChange = () => setReduced(!!mql.matches);
    onChange();
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener?.(onChange);
    return () => mql.removeListener?.(onChange);
  }, []);
  return reduced;
}

/** Home hero quote pairs (owner, 2 Nov 2026): pair 1 = M.T. + M.B., pair 2 = A.B. + D.G. */
export const PROOF_PAIRS = [
  ["mt", "mb"],
  ["ab", "dg"],
];
export const PROOF_INTERVAL_MS = 5000;
export const PROOF_CYCLES = 3;

/**
 * Compact Google proof row for the home hero: "★★★★★ 5.0 on Google · 6 reviews" (fixed,
 * links to the listing) above two real quotes. The quotes come in pairs: every ~5 s the
 * next pair slides in from the right, through all pairs `cycles` times, then it rests on
 * pair 1. Paused while hovered, focused, off screen or in a hidden tab; no auto-advance
 * with prefers-reduced-motion. Dots pick a pair by hand (and stop the rotation). All pairs
 * share one grid cell, so the row keeps the tallest pair's height: nothing shifts.
 */
export function LexiGoogleProofRow({
  reviews = LEXI_GOOGLE_REVIEWS,
  pairs = PROOF_PAIRS,
  intervalMs = PROOF_INTERVAL_MS,
  cycles = PROOF_CYCLES,
  googleUrl = LEXI_GOOGLE_REVIEWS_URL,
  className = "",
}) {
  const reducedMotion = usePrefersReducedMotion();
  const quotePairs = useMemo(
    () =>
      pairs
        .map((ids) =>
          ids
            .map((id) => reviews.find((r) => r.id === id))
            .filter((r) => normalizeReviewText(r?.text)),
        )
        .filter((pair) => pair.length),
    [pairs, reviews],
  );
  const [step, setStep] = useState(0); // automatic moves so far
  const [chosen, setChosen] = useState(null); // pair picked with the dots
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [onScreen, setOnScreen] = useState(true);
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState !== "hidden",
  );
  const rootRef = useRef(null);

  const pairCount = quotePairs.length;
  // 2 pairs x 3 cycles = 6 moves: 1→2→1→2→1→2→1, ending on pair 1
  const lastStep = pairCount > 1 ? pairCount * cycles : 0;
  const active = chosen ?? (pairCount ? step % pairCount : 0);
  const running =
    !reducedMotion &&
    chosen === null &&
    step < lastStep &&
    !hovered &&
    !focused &&
    onScreen &&
    tabVisible;

  useEffect(() => {
    if (!running) return undefined;
    const timer = setTimeout(() => setStep((s) => s + 1), intervalMs);
    return () => clearTimeout(timer);
  }, [running, step, intervalMs]);

  useEffect(() => {
    const onVisibility = () =>
      setTabVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) setOnScreen(entry.isIntersecting);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const count = reviews.length;
  if (!count) return null;
  const average = (
    reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / count
  ).toFixed(1);

  const renderPair = (pair) => (
    <ul className="mx-auto flex w-full max-w-[860px] flex-col items-center justify-center gap-1 md:flex-row md:items-start md:gap-8 text-[13px] md:text-[14px] text-gray-600">
      {pair.map((r) => (
        <li key={r.id} className="text-center md:flex-1">
          <span className="italic text-gray-800">“{stripHtmlBreaks(r.text)}”</span>{" "}
          <span className="whitespace-nowrap">— {r.name}, Google review</span>
        </li>
      ))}
    </ul>
  );

  const moved = step > 0 || chosen !== null;

  return (
    <div
      ref={rootRef}
      className={`flex w-full flex-col items-center gap-2 ${className}`}
      data-testid="google-proof-row"
    >
      <a
        href={googleUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-[14px] text-gray-700 hover:text-brand-pink transition-colors"
        aria-label={`Rated ${average} out of 5 on Google from ${count} reviews`}
        data-gtm-element-id="hero_google-reviews_link-1"
      >
        <StarRow rating={Math.round(Number(average))} sizePx={16} gapPx={2} color="#F30099" />
        <span>
          <span className="font-semibold text-black">{average}</span> on Google ·{" "}
          {count} reviews
        </span>
      </a>

      {pairCount ? (
        <div
          className="grid w-full items-center overflow-hidden"
          role="group"
          aria-label="Google reviews"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
          }}
        >
          {/* every pair, invisible, in the same cell: keeps the tallest pair's height */}
          {quotePairs.map((pair, i) => (
            <div
              key={`size-${i}`}
              aria-hidden="true"
              className="invisible col-start-1 row-start-1"
            >
              {renderPair(pair)}
            </div>
          ))}
          <div
            key={`pair-${active}-${step}-${chosen ?? "auto"}`}
            className={`col-start-1 row-start-1 ${
              moved && !reducedMotion
                ? "animate-in fade-in slide-in-from-right-8 duration-500"
                : ""
            }`}
            data-testid="google-proof-pair"
            data-pair={active + 1}
          >
            {renderPair(quotePairs[active])}
          </div>

          {pairCount > 1 ? (
            <div className="col-start-1 row-start-2 flex items-center justify-center pt-1">
              {quotePairs.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setChosen(i)}
                  aria-label={`Show reviews ${i + 1} of ${pairCount}`}
                  aria-pressed={i === active}
                  className="flex h-6 w-6 items-center justify-center"
                  data-gtm-element-id={`hero_google-reviews_dot-${i + 1}`}
                >
                  <span
                    className={`h-2 w-2 rounded-full transition-colors ${
                      i === active ? "bg-brand-pink" : "bg-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Pixel-fit social proof strip (matches Lexi Pro landing Figma).
 */
export function LexiGoogleReviewsSocialProofStrip({
  reviews = LEXI_GOOGLE_REVIEWS,
  autoplayDelay = 10000,
  className = "",
  maxChars = 70,
  onReadMore,
  readMoreLabel = "Read more",
}) {
  const handleReadMore = (event) => {
    // Only respond to click events to avoid double-firing
    if (event?.type !== "click") return;
    
    console.log("[LexiGoogleReviews] handleReadMore called", event?.type);
    event?.preventDefault?.();
    event?.stopPropagation?.();
    
    // Use setTimeout to ensure scroll happens after event processing
    setTimeout(() => {
      if (typeof onReadMore === "function") {
        console.log("[LexiGoogleReviews] calling onReadMore callback");
        onReadMore();
        return;
      }

      console.log("[LexiGoogleReviews] fallback scroll to #reviews");
      const target = document.getElementById("reviews");
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 0);
  };

  return (
    <Swiper
      modules={[Autoplay, A11y]}
      loop
      autoplay={{ delay: autoplayDelay, disableOnInteraction: false }}
      a11y={{ enabled: true }}
      preventClicks={false}
      preventClicksPropagation={false}
      touchStartPreventDefault={false}
      noSwiping
      noSwipingClass="lexi-no-swiping"
      className={className}
    >
      {reviews.map((r) => {
        const { preview, isTruncated, hasText } = getReviewPreview(r.text, maxChars);

        return (
          <SwiperSlide key={r.id}>
            {hasText ? (
            <div className="relative h-[80px] w-full">
              <InitialsAvatar
                name={r.name}
                sizePx={36}
                className="absolute left-[20px] top-[22px]"
              />

              <div className="absolute left-[75px] top-[22px] w-[329px] h-[38px] text-[14px] font-semibold tracking-[0.14px] leading-[normal] text-black">
                {preview}
                {isTruncated ? (
                  <button
                    type="button"
                    className="lexi-no-swiping ml-[6px] text-[12px] font-semibold text-brand-pink underline underline-offset-2 whitespace-nowrap cursor-pointer relative z-10"
                    onClick={handleReadMore}
                  >
                    {readMoreLabel}
                  </button>
                ) : null}{" "}
                
                <span className="text-black/60 font-medium text-[12px] tracking-normal mr-[6px] whitespace-nowrap">
                  — {r.name}
                </span>
                <span className="align-middle whitespace-nowrap">
                  <StarRow rating={r.rating} sizePx={12} gapPx={2} />
                </span>
              </div>
            </div>
          ) : (
            <div className="h-[80px] w-full flex items-center justify-center gap-[10px] px-[20px]">
              <InitialsAvatar name={r.name} sizePx={36} />

              <div className="flex flex-col items-center justify-center">
                <div className="text-[12px] font-medium text-black/60 text-center flex items-center gap-[6px]">
                  <span className="font-bold">{r.name}</span>
                  <StarRow rating={r.rating} sizePx={18} gapPx={4} />
                </div>
              </div>
            </div>
          )}
          </SwiperSlide>
        );
      })}
    </Swiper>
  );
}

export function LexiGoogleReviewsSection({
  id = "reviews",
  reviews = LEXI_GOOGLE_REVIEWS,
  googleUrl = LEXI_GOOGLE_REVIEWS_URL,
  title = "Reviews",
  subtitle,
  afterReviews,
  className = "",
}) {
  const [modalImage, setModalImage] = useState(null);

  const openModal = useCallback((src) => {
    setModalImage(src);
  }, []);

  const closeModal = useCallback(() => {
    setModalImage(null);
  }, []);

  return (
    <section id={id} className={className}>
      <div className="mx-auto w-full max-w-[430px]">
        <h2 className="text-[18px] font-extrabold text-black">{title}</h2>
        {subtitle ? (
          <p className="mt-1 text-[13px] text-gray-600">{subtitle}</p>
        ) : null}
        <div className="mt-3 grid gap-3">
          {reviews.map((r) => (
            <article
              key={r.id}
              className="border border-gray-200 rounded-2xl p-4 bg-white shadow-sm"
            >
              <div className="flex items-start gap-3">
                <InitialsAvatar name={r.name} sizePx={40} />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="text-[14px] font-extrabold text-black truncate">
                      {r.name}
                    </div>
                    <StarRow rating={r.rating} sizePx={14} gapPx={3} />
                  </div>

                  {normalizeReviewText(r.text) ? (
                    <p className="mt-2 text-[14px] leading-relaxed text-gray-800 whitespace-pre-line">
                      {stripHtmlBreaks(r.text)}
                    </p>
                  ) : null}

                  {Array.isArray(r.media) && r.media.length > 0 ? (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {r.media
                        .filter((m) => m?.type === "image" && m?.src)
                        .map((m) => (
                          <button
                            key={m.src}
                            type="button"
                            onClick={() => openModal(m.src)}
                            className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-pink focus:ring-offset-2 rounded-xl"
                          >
                            <img
                              src={m.src}
                              alt="Review media - click to enlarge"
                              className="w-full aspect-square rounded-xl object-cover border border-gray-200 hover:opacity-90 transition-opacity"
                              loading="lazy"
                            />
                          </button>
                        ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="mt-3 text-center text-[12px] text-gray-500">
          Customers&apos; own words from Google, shortened.{" "}
          <a
            href={googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-brand-pink underline underline-offset-2"
            data-gtm-element-id="reviews_google-full_link-1"
          >
            Read the full reviews on Google
          </a>
        </p>
        {afterReviews ? <div className="mt-3">{afterReviews}</div> : null}
      </div>

      {/* Image modal */}
      {modalImage ? <ImageModal src={modalImage} onClose={closeModal} /> : null}
    </section>
  );
}

